import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useMutationState } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { KeywordLandscapeTable } from "./KeywordLandscapeTable";
import { LandscapeDetailView } from "./LandscapeDetailView";
import { AddLandscapeModal } from "@/components/shared/AddLandscapeModal";
import { Button } from "@/components/ui/button";
import { TabHeader } from "@/components/shared/TabHeader";
import { Plus, RefreshCw, Layers } from "lucide-react";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { ProductSectionEmptyState } from "@/components/product-detail/ProductSectionEmptyState";
import { landscapeApi } from "@/resources/landscape";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { LandscapeTab } from "@/types";
import { useAppData } from "@/contexts/AppDataContext";
import { CreateABTestLocationState } from "@/pages/CreateABTest";
import { productApi } from "@/resources/product";
import { keywordBankRunApi } from "@/resources/keyword-bank-run";
import { isKeywordBankImporting } from "@/utils/keywordBankImporting";
import { RoutePath } from "@/routes";
import { landscapeGroupRowKind } from "@/utils/landscapeRunStatus";
import { searchPhraseKey } from "@/utils/searchPhrase";

type DuplicateRow = { key: string; phrase: string };
type PendingDismissal = { groupKey: string; heroProductId: number };

interface KeywordLandscapesContentV2Props {
  productId: number;
  heroAsin: string;
}

export function KeywordLandscapesContentV2({
  productId,
  heroAsin,
}: KeywordLandscapesContentV2Props) {
  const readOnly = useViewerReadOnly();
  const { selectedBrandId } = useAppData();
  const { landscapeId: landscapeIdParam, productId: productIdParam } = useParams<{
    landscapeId?: string;
    productId?: string;
  }>();
  const navigate = useNavigate();
  const [addLandscapeOpen, setAddLandscapeOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    String(productId),
    selectedBrandId,
  );
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<LandscapeTab>(LandscapeTab.Keyword);

  const {
    data: landscapesData,
    isPending,
    isError: isListError,
    refetch: refetchList,
    isFetching: isListFetching,
  } = landscapeApi.useGetLandscapes(productId);

  const { data: keywordBankRunResponse } = keywordBankRunApi.useGetKeywordBankRun(productId);
  const isImporting = isKeywordBankImporting(keywordBankRunResponse?.run);

  const landscapes = landscapesData?.landscapes || [];

  // Groups whose X is in flight stay off screen: a broadcast refetch can list them again before the
  // delete lands, and the mutation's own variables are the record of that.
  const pendingDismissals = useMutationState<PendingDismissal | undefined>({
    filters: { mutationKey: ["landscapes", "dismiss-group"], status: "pending" },
    select: (mutation) => mutation.state.variables as PendingDismissal | undefined,
  });

  const hasPendingDismissal = pendingDismissals.some((d) => d?.heroProductId === productId);

  // One split, server order kept: the search box filters finished groups only.
  const unfinished = landscapes.filter(
    (l) =>
      landscapeGroupRowKind(l) !== "landscape" &&
      !pendingDismissals.some(
        (d) => d?.heroProductId === productId && d.groupKey === searchPhraseKey(l.prioritySearchPhrase),
      ),
  );
  const filteredLandscapes = landscapes.filter(
    (l) =>
      landscapeGroupRowKind(l) === "landscape" &&
      l.prioritySearchPhrase.toLowerCase().includes(search.toLowerCase()),
  );

  // Duplicate rows are this tab's state (newest first), not the server's: they live as long as the
  // component, and are emptied when the product changes (the route does not remount us).
  const [duplicateRows, setDuplicateRows] = useState<DuplicateRow[]>([]);
  const currentProductIdRef = useRef(productId);
  currentProductIdRef.current = productId;
  useEffect(() => {
    setDuplicateRows([]);
  }, [productId]);
  const addDuplicateRows = useCallback((forProductId: number, phrases: string[]) => {
    if (forProductId !== currentProductIdRef.current) return;
    const batch = phrases.map((phrase) => ({ key: searchPhraseKey(phrase), phrase }));
    const keys = new Set(batch.map((r) => r.key));
    setDuplicateRows((current) => [...batch, ...current.filter((r) => !keys.has(r.key))]);
  }, []);
  const dismissDuplicate = useCallback((key: string) => {
    setDuplicateRows((current) => current.filter((r) => r.key !== key));
  }, []);

  useEffect(() => {
    if (!landscapeIdParam || landscapes.length === 0) return;
    for (const l of landscapes) {
      const match = (l.instances ?? []).find((inst) => inst.id === landscapeIdParam);
      if (match) {
        setSelectedInstanceId(match.id);
        return;
      }
    }
  }, [landscapeIdParam, landscapes]);

  const { data: landscapeData, isFetching: isDetailFetching } =
    landscapeApi.useGetLandscapeDetail(selectedInstanceId);

  const allInstances = useMemo(() => {
    if (!selectedInstanceId) return [];
    for (const landscape of landscapes) {
      const instances = landscape.instances ?? [];
      const match = instances.find((inst) => inst.id === selectedInstanceId);
      if (match) {
        return instances.map((inst) => ({ id: inst.id, label: inst.date }));
      }
    }
    return [];
  }, [selectedInstanceId, landscapes]);

  const handleInstanceChange = (id: string) => {
    setSelectedInstanceId(id);
    if (productIdParam) {
      navigate(`/products/${productIdParam}/landscapes/${id}`);
    }
  };

  const handleBack = () => {
    setSelectedInstanceId(null);
    if (productIdParam) {
      navigate(`/products/${productIdParam}/landscapes`);
    }
  };

  if (selectedInstanceId && !landscapeData && isDetailFetching) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 py-20 min-h-scree">
        <RefreshCw className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading landscape data...</p>
      </div>
    );
  }

  if (selectedInstanceId && landscapeData) {
    return (
      <LandscapeDetailView
        landscapeData={landscapeData}
        selectedInstanceId={selectedInstanceId}
        allInstances={allInstances}
        activeTab={activeTab}
        productId={productId}
        productIdParam={productIdParam}
        isDetailFetching={isDetailFetching}
        shareModalOpen={shareModalOpen}
        readOnly={readOnly}
        onActiveTabChange={setActiveTab}
        onInstanceChange={handleInstanceChange}
        onBack={handleBack}
        onShareModalOpenChange={setShareModalOpen}
      />
    );
  }

  const isEmpty = landscapes.length === 0 && duplicateRows.length === 0 && !isPending;
  // A failed list load with nothing to show: no cached data (a failed background refetch keeps the
  // list) and no local duplicate rows (those are real and stay visible). Never the empty state.
  const hasLoadError = isListError && !landscapesData && duplicateRows.length === 0;

  // AUT-436: one root with a fixed child order -- header, body, modal -- in both states, so the
  // dialog survives the empty -> populated transition without remounting. AUT-437: the shared
  // header is the first child and renders in every state; its action is disabled while the
  // product's keywords import and while the list loads.
  const body = hasLoadError ? (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 px-4 text-center">
      <p className="text-sm text-destructive font-medium">Couldn't load landscapes</p>
      <p className="text-xs text-muted-foreground max-w-md">
        Something went wrong loading landscapes. Try again in a moment.
      </p>
      <Button variant="outline" size="sm" disabled={isListFetching} onClick={() => refetchList()}>
        Try again
      </Button>
    </div>
  ) : isEmpty ? (
    <>
      {isImporting ? (
        <ProductSectionEmptyState
          icon={Layers}
          title="No landscapes generated yet."
          description="Please wait for the product's keywords to fully load before generating a landscape."
          actions={[
            {
              label: "Generate Landscape",
              icon: Plus,
              onClick: () => setAddLandscapeOpen(true),
              disabled: true,
            },
          ]}
        />
      ) : (
        <ProductSectionEmptyState
          icon={Layers}
          title="No landscapes generated yet."
          description="Generate a landscape to compare this product against competitors across a keyword set."
          actions={[
            {
              label: "Generate Landscape",
              icon: Plus,
              onClick: () => setAddLandscapeOpen(true),
            },
          ]}
        />
      )}
    </>
  ) : (
    <>
      <div className="flex items-center gap-3">
        <ClearableInput
          value={search}
          onChange={setSearch}
          placeholder="Search landscapes..."
          className="h-9"
          wrapperClassName="w-64"
          style={{ borderRadius: "8px" }}
        />
        <span className="text-sm text-muted-foreground">
          {isPending
            ? "Loading..."
            : isListFetching
              ? "Updating..."
              : `Showing ${unfinished.length + filteredLandscapes.length} of ${landscapes.length} landscape${landscapes.length !== 1 ? "s" : ""}`}
        </span>
      </div>

      {/* AUT-247: the scrim stays `absolute inset-0` -- dimming the stale rows is the point --
          but the spinner and its caption move OUT of it, into normal flow below the table.
          Centring them inside the scrim is what put them on top of the rows: with a single
          landscape the table is ~100px of header + row, and the block centred in that lands
          squarely on the row. Reserving height does not fix it, it only pushes the block down
          onto the row from the other side -- clearing one row needs ~256px and two rows ~356px,
          so any fixed minimum is wrong for some row count. Out of the scrim it cannot overlap
          at any row count, with no magic number. */}
      <div className="border-t border-border relative">
        {isListFetching && !isPending && (
          <div
            className="absolute inset-0 z-20 bg-background/60 backdrop-blur-sm rounded-lg pointer-events-none"
            aria-hidden="true"
          />
        )}
        <KeywordLandscapeTable
          landscapes={filteredLandscapes}
          unfinishedLandscapes={unfinished}
          duplicateRows={duplicateRows}
          onDismissDuplicate={dismissDuplicate}
          heroAsin={heroAsin}
          heroProductId={productId}
          showAllRows
          isLoading={isPending}
          onViewInstance={(instanceId) => {
            if (productIdParam) {
              navigate(`/products/${productIdParam}/landscapes/${instanceId}`);
            }
            setSelectedInstanceId(instanceId);
          }}
          readOnly={readOnly}
          onCreateAbTest={
            !readOnly
              ? (keyword: string) => {
                  if (!heroAsinData) return;
                  const state: CreateABTestLocationState = {
                    heroAsin: heroAsinData,
                    productId,
                    prefillFromKeyword: { keyword, source: "landscape" },
                    defaultSource: "landscape",
                    returnTo: window.location.pathname + window.location.search,
                  };
                  navigate(RoutePath.CreateABTest, { state });
                }
              : undefined
          }
        />

        {isListFetching && !isPending && (
          <div className="relative z-30 flex flex-col items-center gap-3 py-6">
            <RefreshCw className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Updating landscapes...</p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={isEmpty || hasLoadError ? "min-w-0 space-y-6" : "space-y-4"}>
      <TabHeader title="Landscapes">
        {!readOnly ? (
          <Button disabled={isImporting || isPending || hasLoadError || hasPendingDismissal} onClick={() => setAddLandscapeOpen(true)}>
            <Plus className="h-4 w-4" />
            Add Landscape
          </Button>
        ) : null}
      </TabHeader>
      {body}
      {!readOnly ? (
        <AddLandscapeModal
          open={addLandscapeOpen}
          onOpenChange={setAddLandscapeOpen}
          preselectedAsin={heroAsin}
          heroProductId={productId}
          onDuplicates={(phrases) => addDuplicateRows(productId, phrases)}
        />
      ) : null}
    </div>
  );
}
